/**
 * Measurement rules of the ME report (Bloco 19): Brazilian decimal
 * presentation, numeric parsing and the dimensional laudo.
 *
 * Values are ALWAYS stored as numbers; "6,20" is presentation only.
 */

/** Number of decimals used by every dimensional value of the ME report. */
export const ME_DECIMAL_PLACES = 2;

/** 6 → "6,00" · 6.2 → "6,20" · 8.456 → "8,46". Empty string when there is no value. */
export function formatDecimalBR(value: number | null | undefined, decimals: number = ME_DECIMAL_PLACES): string {
  if (value === null || value === undefined) return "";
  if (typeof value !== "number" || !Number.isFinite(value)) return "";
  return value.toFixed(decimals).replace(".", ",");
}

/**
 * Accepts what a Brazilian user types ("6,20") and what a numeric input
 * produces ("6.20"), returning null when there is no usable number — an
 * empty field must never become 0.
 */
export function parseDecimalBR(input: string | number | null | undefined): number | null {
  if (input === null || input === undefined) return null;
  if (typeof input === "number") return Number.isFinite(input) ? input : null;

  const raw = input.trim();
  if (!raw) return null;

  // "1.234,56" (thousands + decimal comma) → "1234.56"; "6,20" → "6.20".
  const normalized = raw.includes(",")
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Laudo codes of the ME report — mirrors the official legend (section 05). */
export type MeLaudoCode = "A" | "R" | "EC" | "P" | "ADPC";

/**
 * Dimensional laudo: Espessura Atual vs Espessura Mínima, and nothing else.
 * Remaining-life, corrosion rate, API and NACE calculations are independent
 * and are deliberately NOT consulted here.
 *
 * Without both values there is not enough data to judge, so it stays
 * "P — EM ELABORAÇÃO" instead of inventing an approval or a rejection.
 */
export function resolveLaudo(
  minThickness: number | null | undefined,
  actualThickness: number | null | undefined
): MeLaudoCode {
  if (
    minThickness === null || minThickness === undefined || !Number.isFinite(minThickness) ||
    actualThickness === null || actualThickness === undefined || !Number.isFinite(actualThickness)
  ) {
    return "P";
  }
  return actualThickness >= minThickness ? "A" : "R";
}

export const ME_LAUDO_LABELS: Record<MeLaudoCode, string> = {
  A: "A — APROVADO",
  R: "R — REPROVADO",
  EC: "EC — EXAME COMPLEMENTAR",
  P: "P — EM ELABORAÇÃO",
  ADPC: "ADPC — A SER DEFINIDO PELO CLIENTE"
};

/** Symbol prefix so the result never depends on colour alone. */
export const ME_LAUDO_SYMBOLS: Record<MeLaudoCode, string> = {
  A: "✓",
  R: "✕",
  EC: "!",
  P: "…",
  ADPC: "?"
};

/**
 * Which laudos a user may choose for a row, given what the thicknesses
 * calculated. Below the minimum the result is R by default and may only be
 * moved to ADPC (a formal client decision) — never silently "approved".
 * Above the minimum, or without enough data, the calculated value stands.
 */
export function allowedLaudoOverrides(calculated: MeLaudoCode): MeLaudoCode[] {
  return calculated === "R" ? ["R", "ADPC"] : [calculated];
}

export function isLaudoOverrideAllowed(calculated: MeLaudoCode, chosen: MeLaudoCode): boolean {
  return allowedLaudoOverrides(calculated).includes(chosen);
}

/**
 * The laudo the document must print: the user's explicit decision when it is
 * a permitted override, otherwise the calculated result.
 */
export function resolveFinalLaudo(
  minThickness: number | null | undefined,
  actualThickness: number | null | undefined,
  override?: MeLaudoCode | null
): MeLaudoCode {
  const calculated = resolveLaudo(minThickness, actualThickness);
  if (override && isLaudoOverrideAllowed(calculated, override)) return override;
  return calculated;
}

/**
 * Suggested id for a new row ("P01", "P02"...). Only a suggestion: the user
 * may replace it with whatever the croqui uses, and an edited id is never
 * regenerated.
 */
export function suggestNextPointId(existingIds: readonly string[]): string {
  let highest = 0;
  for (const id of existingIds) {
    const match = /^P(\d+)$/i.exec((id || "").trim());
    if (match) {
      const n = parseInt(match[1]!, 10);
      if (Number.isFinite(n) && n > highest) highest = n;
    }
  }
  return `P${String(highest + 1).padStart(2, "0")}`;
}
